import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { PremiumLock } from "@/components/Upgrade";
import { AuthImage } from "@/components/AuthImage";
import { formatDate } from "@/lib/plant";
import { Scale, Star, Sprout, TrendingUp } from "lucide-react";

function Stars({ value }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`h-3.5 w-3.5 ${n <= value ? "fill-amber-400 text-amber-400" : "text-stone-300 dark:text-stone-600"}`} />
      ))}
    </div>
  );
}

export default function Harvests() {
  const { user } = useAuth();
  const [harvests, setHarvests] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.is_premium) api.get("/harvests").then(({ data }) => setHarvests(data.harvests)).catch(() => setHarvests([]));
  }, [user]);

  if (user && !user.is_premium) {
    return (
      <Layout>
        <PremiumLock
          title="Harvest analytics is a Pro feature"
          description="Track yields across every sprout and season, with totals and average ratings."
          features={["Season-by-season yield totals", "Average taste ratings", "Unlimited sprouts & all AI tools"]}
        />
      </Layout>
    );
  }

  const totals = (harvests || []).reduce((acc, h) => {
    if (h.yield_amount) acc[h.yield_unit] = (acc[h.yield_unit] || 0) + h.yield_amount;
    return acc;
  }, {});
  const rated = (harvests || []).filter((h) => h.rating);
  const avgRating = rated.length ? (rated.reduce((s, h) => s + h.rating, 0) / rated.length).toFixed(1) : null;

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Harvest Log</h1>
        <p className="text-muted-foreground mt-1">Every yield you've recorded — compare seasons at a glance.</p>
      </div>

      {!harvests ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-card border border-border animate-pulse" />
          ))}
        </div>
      ) : harvests.length === 0 ? (
        <div className="bg-card rounded-2xl border border-dashed border-border p-12 text-center">
          <Scale className="h-10 w-10 text-orange-400 mx-auto mb-3" />
          <p className="text-muted-foreground">No harvests logged yet. Open a sprout and log your first yield.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="bg-card rounded-2xl border border-border p-5 shadow-sm">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                <Sprout className="h-4 w-4" /> Harvests
              </div>
              <div className="font-display text-3xl font-bold">{harvests.length}</div>
            </div>
            {Object.entries(totals).slice(0, 2).map(([unit, amt]) => (
              <div key={unit} className="bg-card rounded-2xl border border-border p-5 shadow-sm">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  <Scale className="h-4 w-4" /> Total ({unit})
                </div>
                <div className="font-display text-3xl font-bold">{amt}</div>
              </div>
            ))}
            {avgRating && (
              <div className="bg-card rounded-2xl border border-border p-5 shadow-sm">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  <TrendingUp className="h-4 w-4" /> Avg Rating
                </div>
                <div className="font-display text-3xl font-bold flex items-center gap-1">{avgRating} <Star className="h-5 w-5 fill-amber-400 text-amber-400" /></div>
              </div>
            )}
          </div>

          <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden" data-testid="harvest-list">
            {harvests.map((h) => (
              <button
                key={h.id}
                onClick={() => navigate(`/sprouts/${h.plant_id}`)}
                className="w-full flex items-center gap-4 p-4 border-b border-border last:border-0 hover:bg-secondary/40 transition text-left"
                data-testid="harvest-overview-item"
              >
                {h.photo_path ? (
                  <AuthImage path={h.photo_path} className="w-14 h-14 rounded-xl object-cover shrink-0" fallbackClassName="w-14 h-14 rounded-xl" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center shrink-0">
                    <Scale className="h-6 w-6 text-orange-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-semibold">{h.plant_name}</span>
                    {h.plant_species && <span className="text-xs text-muted-foreground">{h.plant_species}</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5">
                    {h.yield_amount != null && <span className="text-sm font-medium text-orange-700 dark:text-orange-300">{h.yield_amount} {h.yield_unit}</span>}
                    {h.rating ? <Stars value={h.rating} /> : null}
                    <span className="text-xs text-muted-foreground">{formatDate(h.date)}</span>
                  </div>
                  {h.taste_notes && <p className="text-sm text-muted-foreground truncate mt-0.5">{h.taste_notes}</p>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}
